import { XYPosition } from '@xyflow/react';
import { useCallback } from 'react';
import { convertXYPositionToGridIndex } from './helpers/gridPositioningUtils';
import { CanvasElementType } from './canvas-types';
import { findRegularNodeAtGridPosition } from './helpers/findRegularNodeAtGridPosition';
import { useSweepCanvasState } from './internal-context/CanvasStateContext';
import { RfNodeRegularNode } from './canvas-types/nodeTypesData';

export enum DropNodeStateEnum {
  INVALID = 'INVALID',
  VALID = 'VALID',
  VALID_SWITCH = 'VALID_SWITCH',
}
export const useCalculateDragAndDrop = () => {
  const { getCanvasNodes } = useSweepCanvasState();

  const getDropNodeState = useCallback(
    ({
      newPotentialNodPos,
      id,
      allowSwap,
    }: {
      newPotentialNodPos: XYPosition;
      id: string;
      allowSwap?: boolean;
    }): {
      dropState: DropNodeStateEnum;
      nodeAtPosition?: RfNodeRegularNode;
    } => {
      const nodes = getCanvasNodes().filter(
        (node) => node.type === CanvasElementType.REGULAR,
      ) as RfNodeRegularNode[];

      const node = nodes.find((node) => node.id === id);
      if (node) {
        const parentId = node?.parentId;

        const newCanvasPos = convertXYPositionToGridIndex(
          {
            x: newPotentialNodPos.x,
            y: newPotentialNodPos.y,
          },
          { withPaddingByNodeType: CanvasElementType.REGULAR },
        );

        const nodeAtNewPos = findRegularNodeAtGridPosition({
          gridPosition: newCanvasPos,
          nodes: nodes,
          compareFn: (node) => node.id !== id && node.parentId === parentId,
        });

        if (nodeAtNewPos) {
          if (allowSwap && node.data.allowNodeSwapping && nodeAtNewPos.data.allowNodeSwapping) {
            return {
              dropState: DropNodeStateEnum.VALID_SWITCH,
              nodeAtPosition: nodeAtNewPos,
            };
          }
          return {
            dropState: DropNodeStateEnum.INVALID,
            nodeAtPosition: nodeAtNewPos,
          };
        }
      }
      return {
        dropState: DropNodeStateEnum.VALID,
      };
    },
    [getCanvasNodes],
  );

  return {
    getDropNodeState,
  };
};
